// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Enter VR and Enter MR (08 §7, M5-01).
//
// Two buttons and a decision. The decision is 08 §7's landing selector, and it mirrors Chess VR's
// exactly, because the states are the same states:
//
// | Visitor | What the button does |
// |---|---|
// | On a headset | Enters the session — the browser shows its own confirmation |
// | On a phone or desktop | `/headset`, which shows a QR code for the same signed-in URL |
// | No WebXR at all | Nothing is shown, because there is nothing to offer |
//
// **The Pro gate is here now, and it arrives with the identity rather than being fetched** (M10-12).
// This component is on the front door, which may not construct a Supabase client (FR-030), so it
// cannot ask what tier the viewer is — the account bundle writes the answer to
// `entitlementSignal.ts` whenever `/entitlement` replies, and this reads it. A cold tab knows
// nothing and is treated as *unknown*, which offers the account page rather than a price.
//
// **The device question is still asked first, and that ordering is the point.** A button that
// checked a subscription before knowing whether the device could present would send a paying player
// on a laptop to a pricing page they do not need — the honest answer there is the QR handoff.
//
// **The signal is a hint about what to offer, never an authority about what is allowed.** It is in
// `localStorage`, so a determined player can edit it into a solo WebXR session of a machine they
// can already play in a window. Everything that would actually cost somebody else something — the
// shared arcade, voice, publishing, the paid roster — is refused by the server regardless.
//
// **The support question is asked once, when the buttons mount.** `isSessionSupported` is a real
// runtime call — on some headsets it wakes the compositor — and asking it per render would ask it
// on every score change. Its answer cannot change while the page is open: a headset does not
// become a laptop.
//
// **Entering must happen inside the click.** WebXR requires a user gesture, and a `requestSession`
// reached through an `await` on something else has usually lost it. So the support check is done
// up front and the click does nothing but enter.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { knownTier } from '../entitlementSignal'
import { gateVerdict } from '../../ui/pricingCatalog'
import type { MachineRuntime } from '../machineRuntime'
import type { ImmersiveMode } from '../../xr/features'
import { useLocalisation } from '../i18n/useLanguage'
import { buildModeOffered } from '../../xr/build/flag'
import { buildWanted } from '../../xr/build/intent'

interface Supported {
  readonly vr: boolean
  readonly ar: boolean
}

export interface EnterHeadsetProps {
  readonly runtime: MachineRuntime | null
  /** True while a session is presenting, so the pair becomes a single Exit. */
  readonly presenting: boolean
  /** Told when a session could not be entered, so the stage can say so. */
  readonly onProblem?: (message: string) => void
  /**
   * Render nothing unless this device can actually present a session (M5-01a).
   *
   * **Set on the stage, and it is a layout fix as much as an editorial one.** These buttons live in
   * `.stage__overlay`, which is `position: absolute; inset: 0` inside the playfield pane with its
   * contents pinned to the bottom — so on a desktop they sit in the dark below a tall machine, and
   * on a phone, where the pane is short, they sit **on the lower playfield**, over the flippers.
   * The overlay's children are `pointer-events: auto`, so that strip also swallowed nudge flicks
   * aimed at the machine.
   *
   * They were worth that cost when they entered a session. On a device that cannot present they do
   * not: `enter` sends the player to `/headset` for a QR code. So the stage was covering the most
   * important part of the machine to offer a handoff — and a handoff is a thing somebody goes
   * looking for between games, not mid-ball. It is on the Settings page instead, next to the
   * account link, where every other "take this elsewhere" action already lives.
   *
   * The rule is the device rather than the viewport, deliberately: "can this thing present" is the
   * question that actually decides whether the button does something, and a width is a guess at it.
   */
  readonly onlyWhenPresentable?: boolean
}

export function EnterHeadset({
  runtime,
  presenting,
  onProblem,
  onlyWhenPresentable = false,
}: EnterHeadsetProps): React.JSX.Element | null {
  const { t } = useLocalisation()
  const [supported, setSupported] = useState<Supported | undefined>(undefined)
  const navigate = useNavigate()

  /**
   * Read once, on mount.
   *
   * Not per render, and not live: the value changes when somebody signs in or subscribes, and both
   * of those are navigations away from this page and back. A `storage` listener here would be a
   * subscription kept alive for the length of every game to catch an event that cannot happen
   * while the game is on screen.
   */
  const [tier] = useState(() => knownTier())
  /**
   * Did the Builder's door send them here (M13)? Read once, the same way, and a peek rather than
   * a take: the session takes the intent when it starts. The buttons say what they will do —
   * "Build in MR" — because a button that says "Enter MR" and lands at a bench is a surprise, and
   * one that says "Build" and lands in a game is the surprise the first author reported.
   */
  const [building] = useState(() => buildModeOffered() && buildWanted())

  useEffect(() => {
    if (runtime === null) return
    let live = true
    void (async () => {
      const [vr, ar] = await Promise.all([
        runtime.supportsXr('immersive-vr'),
        runtime.supportsXr('immersive-ar'),
      ])
      // The component may have unmounted while the runtime was asking; setting state then is a
      // React warning and, worse, a promise holding a reference to a disposed machine.
      if (live) setSupported({ vr, ar })
    })()
    return () => {
      live = false
    }
  }, [runtime])

  const enter = useCallback(
    (mode: ImmersiveMode) => {
      void (async () => {
        if (runtime === null) return
        // On a device that cannot present, the offer is a handoff rather than a refusal (08 §7).
        // **Asked before the subscription**, so a Pro player on a laptop gets the QR code and not a
        // price, and a free player on a laptop gets the QR code too — they will meet the gate on
        // the headset, where it is about the thing in front of them.
        if (supported === undefined || !(mode === 'immersive-vr' ? supported.vr : supported.ar)) {
          await navigate(`/headset?returnTo=${encodeURIComponent(globalThis.location.pathname)}`)
          return
        }

        // **Pro (M10-12).** Two cases, one destination. A tier that says no is a refusal with a
        // reason; a tier that is *unknown* is a cold tab that has never asked. Both go to the
        // account page, which loads the bundle, resolves the entitlement and remembers it — so a
        // subscriber pays for it once, on the first game of a fresh tab, and a free player sees
        // what a subscription is for on the page that is about their account.
        //
        // **Never a refusal in place.** A button that says "no" on the stage is a button somebody
        // presses twice; one that goes somewhere is one they can act on.
        if (tier === undefined || gateVerdict('headset', tier, false) !== 'allowed') {
          await navigate('/account?want=headset')
          return
        }
        if (!(await runtime.enterXr(mode))) {
          onProblem?.(t('xr.no-session'))
        }
      })()
    },
    // `t` is listed because the callback reports a failure in the reader's language: it is stable
    // per language, so this rebuilds the handler when somebody switches and never otherwise.
    [runtime, supported, navigate, onProblem, tier, t],
  )

  const leave = useCallback(() => {
    void runtime?.exitXr()
  }, [runtime])

  // Nothing at all until the answer is in. A pair of buttons that appear and then change what
  // they do is worse than a pair that arrive a moment late.
  if (runtime === null || supported === undefined) return null

  // On a caller that asked for it, a device that cannot present gets nothing here (M5-01a). Not
  // disabled and not a smaller button: the offer is genuinely elsewhere, and a control on the
  // glass that means "go to another page" is the thing that was covering the flippers.
  //
  // `presenting` is checked first below, so this can never hide the Exit — a device that is
  // presenting has, by definition, answered yes to one of the two.
  if (onlyWhenPresentable && !presenting && !supported.vr && !supported.ar) return null

  if (presenting) {
    return (
      <div className="stage__xr">
        <button
          type="button"
          className="button button--quiet"
          title={t('xr.exit-title')}
          onClick={leave}
        >
          {t('xr.exit')}
        </button>
      </div>
    )
  }

  return (
    <div className="stage__xr" role="group" aria-label={t('settings.headsets-link')}>
      <button
        type="button"
        className="button"
        title={!supported.vr ? t('xr.handoff-title') : building ? t('xr.build-title') : t('xr.vr-title')}
        onClick={() => enter('immersive-vr')}
      >
        {building ? t('xr.build-vr') : t('xr.enter-vr')}
      </button>
      <button
        type="button"
        className="button"
        title={!supported.ar ? t('xr.handoff-title') : building ? t('xr.build-title') : t('xr.mr-title')}
        onClick={() => enter('immersive-ar')}
      >
        {building ? t('xr.build-mr') : t('xr.enter-mr')}
      </button>
    </div>
  )
}
